import NewSellerBoxLogoCircle from '@/assets/icons/NewSellerBoxLogoCircle.svg?react';
import SellerBoxLogoCircleGradient from '@/assets/icons/SellerBoxLogoCircleGradient.svg?react';

import Button from '@/components/atoms/Button';
import { useChatbotActions } from '@/hooks/V3/useChatbotActions';
import { Message } from '@/types/discover';
import { cn } from '@/utils/tailwind';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChatbotContext } from '../../_context/ChatbotContext';

import { extractActionButtons } from '@/utils/extractActionButtons';
import { formatTableCellContent } from '@/utils/formatTableCellContent';

interface BotBubbleProps {
  data?: Message;
  isDashboard?: boolean;
}

const AiChatbotBubble = ({ data, isDashboard }: BotBubbleProps) => {
  const { messages } = useChatbotContext();
  const { handleSendMessage } = useChatbotActions('action_button');

  const { buttons, cleanedText } = data?.text
    ? extractActionButtons(data.text)
    : { buttons: [], cleanedText: '' };

  const isLastMessage = data && messages && messages[messages.length - 1]?.id === data.id;

  return (
    <div className="flex w-full flex-col gap-[10px]">
      {/* 헤더 */}
      <div
        className={cn('flex  gap-[8px]', {
          'items-center': isDashboard,
          'items-end': !isDashboard,
        })}
      >
        <SellerBoxLogoCircleGradient width={isDashboard ? 20 : 32} height={isDashboard ? 20 : 32} />
        <span className=" text-gr-900 Body5M14">셀러박스 AI</span>
      </div>

      {/* 메시지 컨텐츠 */}
      <div
        className={cn(
          'w-fit max-w-[550px] whitespace-pre-wrap break-words rounded-[12px] p-[12px] text-gr-800 Body5M14',
          isDashboard ? 'bg-gr-50' : 'bg-gr-100',
        )}
      >
        <MarkdownRenderer text={cleanedText} />
      </div>

      {/* 유도 버튼 */}
      {buttons.length > 0 && isLastMessage && (
        <ActionButtons buttons={buttons} onClick={handleSendMessage} />
      )}
    </div>
  );
};

export default AiChatbotBubble;

/* ------------------------------
 * Markdown 렌더링 컴포넌트
 * ------------------------------ */
const MarkdownRenderer = ({ text }: { text: string }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      h3: ({ children }) => <h3 className="mt-3 text-lg font-bold">{children}</h3>,
      p: ({ children }) => <p>{children}</p>,
      ul: ({ children }) => (
        <ul className="flex list-inside list-disc flex-col gap-1">{children}</ul>
      ),
      li: ({ children }) => <li className="ml-4">{children}</li>,
      ol: ({ children }) => (
        <ol className="flex list-inside list-decimal flex-col gap-1">{children}</ol>
      ),
      table: ({ children }) => (
        <div className="scrollbar w-full overflow-x-auto">
          <table className="w-full border-collapse border border-gr-300 shadow-sm">
            {children}
          </table>
        </div>
      ),
      thead: ({ children }) => (
        <thead className="rounded-t-md border-b border-gr-400 bg-gr-200 uppercase text-gr-700">
          {children}
        </thead>
      ),
      tbody: ({ children }) => (
        <tbody className="divide-y divide-gr-300 rounded-b-md">{children}</tbody>
      ),
      tr: ({ children }) => (
        <tr className="transition even:bg-gr-50 hover:bg-gr-100">{children}</tr>
      ),
      th: ({ children }) => (
        <th className="whitespace-nowrap border border-gr-400 px-4 py-3 text-center text-xs font-semibold md:text-sm lg:text-base">
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="overflow-hidden text-ellipsis whitespace-nowrap border border-gr-300 px-4 py-3 text-left text-xs text-gr-800 md:text-sm lg:text-base">
          {formatTableCellContent(children)}
        </td>
      ),
    }}
  >
    {text || '일시적으로 오류가 발생하였습니다.\n오류가 반복되면 1:1 문의를 남겨주세요.'}
  </ReactMarkdown>
);

/* ------------------------------
 * 액션 버튼 컴포넌트
 * ------------------------------ */
const ActionButtons = ({
  buttons,
  onClick,
}: {
  buttons: { label: string; action: string }[];
  onClick: (messageText: string, action?: string) => void;
}) => (
  <div className="flex flex-wrap gap-2">
    {buttons.map(({ label, action }, index) => (
      <Button
        key={index}
        variant="outline"
        className="px-4 py-2 text-gr-800"
        onClick={() => onClick(label, action)}
      >
        {label}
      </Button>
    ))}
  </div>
);
